import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import AuthActions, { thunks as AuthThunks } from 'reducers/Auth'
import { AUTH_ERRORS } from 'constants/enums'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import useLocalStorage from 'utils/hooks/useLocalStorage'
import AuthLayout from './AuthLayout'
import LoginRolloutBoundary from './LoginWithRollout'
import LoginCard from './components/LoginCard'
import LoginForm from './components/LoginForm'
import MagicLinkForm from './components/MagicLinkForm'

const Login = ({
  loggedIn,
  responsiveBreakpointsPage = RESPONSIVE_BREAKPOINT_PAGES.LOGIN,
  responsiveBreakpointsRollout = false
}) => {
  const params = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const authentication = useSelector((state) => state.authentication)
  const [isSignInFailed, setIsSignInFailed] = useState(false)
  const [isMagicLinkFlow, setIsMagicLinkFlow] = useState(false)
  const [error, setError] = useState('NONE')
  const [, setConnectionToken] = useLocalStorage(
    'connectionToken',
    ''
  )

  const onLoginWithPasswordSubmit = async (values) => {
    try {
      await dispatch(
        AuthThunks.loginWithPassword({
          email: values.email,
          password: values.password,
          publicDevice: values.publicDevice
        })
      )
      navigate('/releases')
    } catch (caughtError) {
      const { data } = caughtError
      console.error(caughtError?.data)
      setIsSignInFailed(true)
      if (data?.code === 401) {
        setError(AUTH_ERRORS.INVALID_CREDENTIALS)
      }

      if (data?.code === 425) {
        setError(AUTH_ERRORS.VERIFICATION_PENDING)
      }
    }
  }

  const onLoginWithMagicLinkSubmit = async (values) => {
    try {
      await dispatch(
        AuthThunks.requestLoginEmail({
          email: values.email,
          publicdevice: values.publicDevice
        })
      )
    } catch (caughtError) {
      console.error(caughtError?.data)
    }
  }
  //Magic link flow
  useEffect(() => {
    const initialize = async () => {
      const { token } = params

      if (token) {
        try {
          await dispatch(AuthThunks.initialSetup({ token, navigate }))
        } catch (caughtError) {
          console.error('Error during initial setup:', caughtError)
        }
      } else {
        dispatch(AuthActions.clearLoginMessage())
      }
    }

    initialize()
  }, [])

  // Connection token flow
  useEffect(() => {
    const { signInConnectionToken } = params

    if (signInConnectionToken) {
      setConnectionToken(signInConnectionToken)
    }
  }, [])

  if (loggedIn) {
    return <Navigate to="/releases" />
  }

  const content = (
    <AuthLayout>
      <LoginCard>
        {isMagicLinkFlow ? (
          <MagicLinkForm
            setIsMagicLinkFlow={setIsMagicLinkFlow}
            onSubmit={onLoginWithMagicLinkSubmit} />
        ) : (
          <LoginForm
            isSignInFailed={isSignInFailed}
            error={error}
            isLoading={authentication.isLoading}
            isEmailSent={authentication.isEmailSent}
            setIsMagicLinkFlow={setIsMagicLinkFlow}
            onSubmit={onLoginWithPasswordSubmit} />
        )}
      </LoginCard>
    </AuthLayout>
  )

  return responsiveBreakpointsRollout ? (
    <LoginRolloutBoundary page={responsiveBreakpointsPage}>
      {content}
    </LoginRolloutBoundary>
  ) : (
    content
  )
}

Login.propTypes = {
  loggedIn: PropTypes.bool,
  responsiveBreakpointsPage: PropTypes.string,
  responsiveBreakpointsRollout: PropTypes.bool
}

export default Login
